import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import type { PolysemeVariant } from '../../tokens/';
import type { PolysemeSize } from '../../tokens/variants';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { cva } from 'class-variance-authority';

import { Icon, type IconName } from '../icons';

export const badgeVariants = [
  'default',
  'secondary',
  'outline',
  'destructive',
  'info',
  'success',
  'warning',
  'error',
  'neutral',
  'beta',
] as const satisfies readonly PolysemeVariant[];

export type BadgeVariants = (typeof badgeVariants)[number];

export const badgeSizes = [
  'md',
  'lg',
] as const satisfies readonly PolysemeSize[];

export type BadgeSizes = (typeof badgeSizes)[number];

const indicatorVariants = cva('block size-2 rounded-full', {
  variants: {
    variant: {
      default: 'bg-primary-foreground',
      secondary: 'bg-secondary-foreground',
      outline: 'bg-foreground',
      destructive: 'bg-destructive-foreground',
      info: 'bg-semantic-info-foreground',
      success: 'bg-semantic-success-foreground',
      warning: 'bg-semantic-warning-foreground',
      error: 'bg-semantic-error-foreground',
      neutral: 'bg-secondary-foreground',
      beta: 'bg-semantic-beta-foreground',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const badgeStyleVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-transparent py-0.5 font-semibold',
  {
    variants: {
      size: {
        md: 'px-2.5 text-xs',
        lg: 'px-3 text-sm',
      },
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        outline: 'border-border bg-transparent text-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        info: 'bg-semantic-info-background text-semantic-info-foreground',
        success:
          'bg-semantic-success-background text-semantic-success-foreground',
        warning:
          'bg-semantic-warning-background text-semantic-warning-foreground',
        error: 'bg-semantic-error-background text-semantic-error-foreground',
        neutral:
          'bg-semantic-neutral-background text-semantic-neutral-foreground',
        beta: 'bg-semantic-beta-background text-semantic-beta-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export type BadgeProps = ComponentProps<'span'> &
  VariantProps<typeof badgeStyleVariants> & {
    icon?: IconName;
    showIndicator?: boolean;
    srLabel?: string;
  };

export const Badge = ({
  children,
  className,
  icon,
  showIndicator,
  srLabel,
  size = 'md',
  variant = 'default',
  ...props
}: BadgeProps) => {
  return (
    <span
      className={cn(badgeStyleVariants({ variant, size }), className)}
      {...props}
    >
      {!!srLabel && <span className="sr-only">{srLabel}</span>}
      {!!showIndicator && (
        <div
          className={cn(
            indicatorVariants({
              variant,
            })
          )}
        />
      )}
      {!!icon && <Icon name={icon} size="sm" />}
      {children}
    </span>
  );
};

export const BadgeComingSoon = ({
  children,
  className,
  size = 'md',
}: Pick<BadgeProps, 'children' | 'className' | 'size'>) => (
  <Badge
    className={className}
    icon="calendar-clock"
    size={size}
    variant="warning"
  >
    {children}
  </Badge>
);

export const BadgeNew = ({
  children,
  className,
  size = 'md',
}: Pick<BadgeProps, 'children' | 'className' | 'size'>) => (
  <Badge
    className={className}
    icon="party-popper"
    size={size}
    variant="success"
  >
    {children}
  </Badge>
);

export const BadgeBeta = ({
  children,
  className,
  size = 'md',
}: Pick<BadgeProps, 'children' | 'className' | 'size'>) => (
  <Badge
    className={className}
    icon="test-tube-diagonal"
    size={size}
    variant="beta"
  >
    {children}
  </Badge>
);
